/**
 * Promotion filtering & sorting for collection/search product grids.
 *
 * Shopify has no native "on sale" filter and no native way to sort a collection by a computed discount
 * (percentage or dollar amount off), so both are applied here on the client against whatever page of
 * results the server just rendered. The "On sale" toggle and the two "Discount" sort options are plain
 * inputs living inside the theme's existing <form is="facet-form">, so they already ride along in the
 * facet request URL — this script just reacts to that URL after each render.
 *
 * Hooks into the theme's own facet lifecycle: every filter/sort/pagination change dispatches a bubbling
 * `facet:update` event and the theme's built-in listener swaps in freshly rendered HTML via
 * `shopifySection.replaceChildren(...)`. Rather than depending on that internal event, we watch the DOM
 * directly with a MutationObserver so this keeps working even if that implementation changes.
 */

const DISCOUNT_SORT_KEYS = {
  'discount-percentage-descending': 'percentage',
  'discount-amount-descending': 'amount',
};

function getCardDiscount(card) {
  const price = Number(card.dataset.price);
  const compareAtPrice = Number(card.dataset.compareAtPrice);

  if (!compareAtPrice || !(compareAtPrice > price)) {
    return null;
  }

  const amount = compareAtPrice - price;

  return { amount, percentage: amount / compareAtPrice };
}

function getActiveState() {
  const params = new URLSearchParams(window.location.search);
  const sortBy = params.get('sort_by');

  return {
    onSaleOnly: params.get('on_sale') === '1',
    sortBy,
    sortKey: DISCOUNT_SORT_KEYS[sortBy] || null,
  };
}

function updateResults(section, state) {
  section.querySelectorAll('.product-list').forEach((productList) => {
    const cards = Array.from(productList.children).filter((el) => el.tagName === 'PRODUCT-CARD');
    const promoBlocks = Array.from(productList.children).filter((el) => el.classList.contains('product-list__promo'));
    const customViewActive = state.onSaleOnly || Boolean(state.sortKey);

    cards.forEach((card) => {
      card.hidden = state.onSaleOnly && !getCardDiscount(card);
    });

    // Promo blocks are positioned for the default sort order; suppress them rather than let them land
    // in a confusing spot once cards are hidden or reordered.
    promoBlocks.forEach((block) => {
      block.hidden = customViewActive;
    });

    if (state.sortKey) {
      const sortKey = state.sortKey;
      const sortedCards = cards.slice().sort((a, b) => {
        const discountA = getCardDiscount(a);
        const discountB = getCardDiscount(b);
        const valueA = discountA ? discountA[sortKey] : -1;
        const valueB = discountB ? discountB[sortKey] : -1;

        return valueB - valueA;
      });

      productList.append(...sortedCards);
    }
  });
}

function updateControls(section, state) {
  section.querySelectorAll('input[name="on_sale"]').forEach((input) => {
    input.checked = state.onSaleOnly;
  });

  if (!state.sortBy) {
    return;
  }

  section.querySelectorAll('input[name="sort_by"]').forEach((input) => {
    input.checked = input.value === state.sortBy;
  });

  section.querySelectorAll('.popover-listbox [role="option"]').forEach((option) => {
    const selected = option.getAttribute('value') === state.sortBy;
    option.setAttribute('aria-selected', selected ? 'true' : 'false');

    if (!selected || !option.dataset.label) {
      return;
    }

    const listbox = option.closest('[aria-owns]');
    const labelTarget = listbox && document.getElementById(listbox.getAttribute('aria-owns'));

    if (labelTarget) {
      labelTarget.textContent = option.dataset.label;
    }
  });
}

function refresh() {
  const state = getActiveState();

  document.querySelectorAll('.shopify-section').forEach((section) => {
    if (!section.querySelector('.product-list')) {
      return;
    }

    updateResults(section, state);
    updateControls(section, state);
  });
}

function observe() {
  const observer = new MutationObserver(() => {
    observer.disconnect();
    refresh();
    observer.observe(document.body, { childList: true, subtree: true });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

refresh();
observe();
